"use client";

import { c as _c } from "react/compiler-runtime";
import { CarePlanContext, useCarePlanContext } from "../care-plan";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useMemo } from "react";
import { CarePlanPrintContext } from "./care-plan-print-context";
import { carePlanTagQueryOptions } from "@/api/provider/settings/care-plan-tag-query";
import { useSuggestions } from "@/api/query/provider/suggestions";
export function useCarePlanPrintSnapshotValue(orientation) {
  const $ = _c(15);
  const context = useCarePlanContext();
  let t0;
  if ($[0] !== context.state) {
    t0 = {
      ...context.state,
      visibleSteps: context.state.steps,
      selectedStepId: null,
      selectedStep: null
    };
    $[0] = context.state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== context.config || $[3] !== orientation) {
    t1 = {
      ...context.config,
      readOnly: true,
      detailMode: "sidebar" as const,
      size: "md" as const,
      orientation
    };
    $[2] = context.config;
    $[3] = orientation;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== context.cycles) {
    t2 = {
      ...context.cycles,
      onlyShowCurrentPasses: false,
      highlightedCycleId: null,
      cycleCreation: null
    };
    $[5] = context.cycles;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== context.sequenceCopy) {
    t3 = {
      ...context.sequenceCopy,
      state: null
    };
    $[7] = context.sequenceCopy;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== context || $[10] !== t0 || $[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = {
      ...context,
      state: t0,
      config: t1,
      cycles: t2,
      sequenceCopy: t3
    };
    $[9] = context;
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/** A thin projection of the existing provider, never a second graph runtime. */
export function CarePlanPrintSnapshot(t0) {
  const $ = _c(5);
  const {
    orientation,
    children
  } = t0;
  const value = useCarePlanPrintSnapshotValue(orientation);
  let t1;
  if ($[0] !== children) {
    t1 = <CarePlanPrintContext.Provider value={true}>{children}</CarePlanPrintContext.Provider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== value) {
    t2 = <CarePlanContext.Provider value={value}>{t1}</CarePlanContext.Provider>;
    $[2] = t1;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function useCarePlanPrintReadiness() {
  const $ = _c(6);
  const context = useCarePlanContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = carePlanTagQueryOptions.list();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tags = useQuery(t0);
  let t1;
  if ($[1] !== context.lookups.patientId) {
    t1 = context.lookups.patientId ? ["PATIENT", "TENANT"] : ["TENANT"];
    $[1] = context.lookups.patientId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const suggestions = useSuggestions(t1, context.lookups.patientId);
  const t2 = context.lookups.isLookupsLoading || tags.isLoading || suggestions.isLoading;
  const t3 = context.lookups.lookupError || tags.error || suggestions.error;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      loading: t2,
      error: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}